import { useEffect, useContext, useState, useRef, useMemo } from "react";
import { MapContainer, Marker, Popup, TileLayer, CircleMarker, useMapEvents, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet.heat";
import L from "leaflet";

import { ContainerMarkerSVG } from "components/waste-icons/svgs/ContainerMarkerSVG";
import WasteSelectorTypes from "components/waste-icons/WasteSelectorTypes";
import AuthContext from "context/AuthContext";
import axiApi from "ApiCaller";
import { SwalAlertClass } from "core/swalClass";
import MapResizeZoom from "components/maps/MapResizeZoom";
import { Link } from "react-router-dom";
import AzureMapBase from "components/maps/azure/AzureMapBase";
import AzureMapMarker from "components/maps/azure/AzureMapMarker";
import AzureMapPopup from "components/maps/azure/AzureMapPopup";
import FitIslands, { LeafletFitIslandsWrapper } from "components/maps/FitIslands";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { renderToString } from "react-dom/server";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import {CARTO_TILE_URL, CARTO_TILE_OPTIONS} from 'core/mapsConfig';

const LeafletHeatmapLayer = ({ points, max, radius, blur, gradient, longitudeExtractor, latitudeExtractor, intensityExtractor }) => {
    const map = useMap();
    const heatLayerRef = useRef(null);

    useEffect(() => {
        if (!map || !points) return;

        const heatPoints = points.map(p => [
            latitudeExtractor(p),
            longitudeExtractor(p),
            intensityExtractor(p)
        ]);

        if (!heatLayerRef.current) {
            heatLayerRef.current = L.heatLayer(heatPoints, {
                radius: radius,
                blur: blur,
                max: max,
                maxZoom: 0, // prevents leaflet.heat from reducing intensity at lower zooms
                minOpacity: 0.1,
                gradient: gradient
            }).addTo(map);
        } else {
            heatLayerRef.current.setOptions({ radius, blur, max, maxZoom: 0, minOpacity: 0.1, gradient });
            heatLayerRef.current.setLatLngs(heatPoints);
        }
    }, [map, points, max, radius, blur, gradient]); // exclude extractors to avoid unnecessary re-renders

    useEffect(() => {
        return () => {
            if (heatLayerRef.current && map) {
                map.removeLayer(heatLayerRef.current);
                heatLayerRef.current = null;
            }
        };
    }, [map]);

    return null;
};

const LeafletZoomTracker = ({ onZoomChange }) => {
    const map = useMapEvents({
        zoomend: () => {
            onZoomChange(map.getZoom());
        },
    });
    return null;
};

const getOffsetCoords = (centerCoords, index, total, radius = 0.00015) => {
    if (total <= 1) return centerCoords;
    const angle = (index / total) * Math.PI * 2;
    return [
        centerCoords[0] + radius * Math.cos(angle),
        centerCoords[1] + radius * Math.sin(angle)
    ];
};


const MapHomeWaste = ({ range }) => {
    let { user, panel } = useContext(AuthContext);
    const Alert = new SwalAlertClass({ confirmButtonText: "Aceptar" });
    const ZOOM = 14;
    const position = user.panel.coords;
    const [islands, setIslands] = useState({});
    const [azureMap, setAzureMap] = useState(null);
    const [currentZoom, setCurrentZoom] = useState(ZOOM);
    const [showDensityLayer, setShowDensityLayer] = useState(false);
    const [showWithoutDevices, setShowWithoutDevices] = useState(false);

    const heatmapPoints = useMemo(() => {
        return Object.values(islands).map(island => {
            const maxFilling = island.assets.length > 0 ? Math.max(...island.assets.map(a => a.filling || 0)) : 0;
            return [island.coords[0], island.coords[1], maxFilling];
        });
    }, [islands]);

    const getDynamicRadius = (zoom) => {
        const minZoom = 8;
        const maxZoom = 18;
        const minRadius = 2;
        const maxRadius = 60;
        const t = Math.min(1, Math.max(0, (zoom - minZoom) / (maxZoom - minZoom)));
        return minRadius + (maxRadius - minRadius) * Math.pow(t, 1.5);
    };
    const dynamicRadius = getDynamicRadius(currentZoom);

    const getHeatmapColor = (filling) => {
        if (filling <= 50) return "#28a745"; // Green
        if (filling <= 80) return "#ffc107"; // Yellow
        return "#dc3545"; // Red
    };
    const urlStatic = process.env?.REACT_APP_STATIC_URL || "/static";

    const getWaypointIconData = (type) => {
        let prefix = "Plastic-Icon";

        if (type === "pc") {
            prefix = "Paper-Icon";
        } else if (type === "pg") {
            prefix = "Plastic-Icon";
        } else if (type === "gl") {
            prefix = "Vidrio-Icon";
        }

        const typeInfo = WasteSelectorTypes[type] || WasteSelectorTypes['pg'];

        return {
            url: `${urlStatic}/images/icons/waypoints/${prefix}.svg`,
            fillColor: typeInfo.backgroundColor || "#989898",
            borderColor: typeInfo.borderColor || "#2c2c2c",
            backgroundColor: "#fff"
        };
    };

    // Misma técnica que AzureMapsRouteCalculator: useState dispara useEffect
    // cuando el mapa O los datos estén listos (lo que llegue después)

    const handleAzureMapReady = (mapInstance) => {
        setAzureMap(mapInstance);
        setCurrentZoom(mapInstance.getCamera().zoom);
        mapInstance.events.add('zoomend', () => {
            setCurrentZoom(mapInstance.getCamera().zoom);
        });
    };

    // El centrado Azure/Leaflet se delega ahora en el componente externo `FitIslands`

    useEffect(() => {
        axiApi
            .get("/api/home/map/", {
                params: { min: range.min, max: range.max, show_without_devices: showWithoutDevices }
            })
            .then((resp) => {
                if (resp.status) {
                    let items = {};
                    resp.data.items.forEach((asset) => {
                        if (!(asset.island_id in items)) {
                            items[asset.island_id] = {
                                name: asset.island_name,
                                coords: [asset.island_lat, asset.island_lng],
                                assets: [],
                            };
                        }
                        items[asset.island_id].assets.push({
                            id: asset.id,
                            uid: asset.uid,
                            wasteId: asset.waste_id,
                            device: asset.has_device,
                            filling: asset.filling,
                            type: asset.waste_type,
                        });
                    });
                    setIslands(items);
                    // console.log("islands", items);
                } else {
                    Alert.error("Server Error", "Se ha encontrado un error en la carga del mapa.");
                }
            })
            .catch((error) => {
                console.info(error);
                Alert.error("Server Error", "Se ha encontrado un error en la carga del mapa.");
            });
    }, [range, showWithoutDevices]);

    switch (panel.map_type) {
        case "az":
            return (
                <>
                    <div className="d-flex justify-content-end mb-2">
                        <div className="form-check form-switch">
                            <input
                                className="form-check-input cursor-pointer"
                                type="checkbox"
                                role="switch"
                                id="showWithoutDevicesSwitchAz"
                                checked={showWithoutDevices}
                                onChange={(e) => setShowWithoutDevices(e.target.checked)}
                            />
                            <label className="form-check-label cursor-pointer ms-2" htmlFor="showWithoutDevicesSwitchAz">
                                Mostrar Sin Dispositivos
                            </label>
                        </div>
                    </div>
                    <AzureMapBase center={position} zoom={12} height="400px" onMapReady={handleAzureMapReady}>
                        <FitIslands islands={islands} azureMap={azureMap} />
                        {Object.entries(islands).map(([key, island]) => (
                            <AzureMapMarker position={island.coords} key={key}>
                                <AzureMapPopup>
                                    <div className="bg-white p-2 h-[200px] rounded">
                                        <p>
                                            <Link to={"/data-view/island/" + key + "/"} className="slx-link">
                                                {island.name}
                                            </Link>
                                        </p>
                                        {island.assets.map((as, ix) => (
                                            <div className="d-inline-block" key={as.id + "-" + ix}>
                                                {as.device ? (
                                                    <Link to={"/data-view/data-container/" + as.uid + "/"}>
                                                        <ContainerMarkerSVG type={as.type} percent={as.filling} />
                                                    </Link>
                                                ) : (
                                                    <ContainerMarkerSVG type={as.type} percent={as.filling} />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </AzureMapPopup>
                            </AzureMapMarker>
                        ))}
                    </AzureMapBase>
                </>
            );
        case "lf":
            return (
                <>
                    <div className="d-flex justify-content-end mb-2 gap-3 align-items-center">
                        <div className="form-check form-switch">
                            <input
                                className="form-check-input cursor-pointer"
                                type="checkbox"
                                role="switch"
                                id="showWithoutDevicesSwitchLf"
                                checked={showWithoutDevices}
                                onChange={(e) => setShowWithoutDevices(e.target.checked)}
                            />
                            <label className="form-check-label cursor-pointer ms-2" htmlFor="showWithoutDevicesSwitchLf">
                                Mostrar Sin Dispositivos
                            </label>
                        </div>
                        <div className="form-check form-switch">
                            <input
                                className="form-check-input cursor-pointer"
                                type="checkbox"
                                role="switch"
                                id="densityHeatmapSwitchLf"
                                checked={showDensityLayer}
                                onChange={(e) => setShowDensityLayer(e.target.checked)}
                            />
                            <label className="form-check-label cursor-pointer ms-2" htmlFor="densityHeatmapSwitchLf">
                                Ver Capa de Calor
                            </label>
                        </div>
                    </div>
                    <MapContainer
                        center={position}
                        zoom={ZOOM}
                        scrollWheelZoom={true}
                        style={{ width: "100%", height: "500px", zIndex: "2" }}>
                        <MapResizeZoom position={position} zoomDefault={ZOOM} />
                        <LeafletFitIslandsWrapper islands={islands} />
                        <LeafletZoomTracker onZoomChange={setCurrentZoom} />
                        <TileLayer
                            url={CARTO_TILE_URL}
                            {...CARTO_TILE_OPTIONS}
                        />
                        {showDensityLayer && (
                            <LeafletHeatmapLayer
                                key={`heatmap-${currentZoom}`}
                                points={heatmapPoints}
                                longitudeExtractor={m => m[1]}
                                latitudeExtractor={m => m[0]}
                                intensityExtractor={m => parseFloat(m[2])}
                                max={100}
                                radius={dynamicRadius}
                                blur={dynamicRadius * 0.8}
                                gradient={{
                                    0.1: 'rgba(40, 167, 69, 0.5)',
                                    0.5: '#28a745',
                                    0.65: '#ffc107',
                                    0.9: '#dc3545'
                                }}
                            />
                        )}
                        {!showDensityLayer && (
                            <MarkerClusterGroup
                                disableClusteringAtZoom={14}
                                iconCreateFunction={(cluster) => {
                                    const markers = cluster.getAllChildMarkers();
                                    let clusterMaxFilling = 0;
                                    let clusterHasWarning = false;
                                    let clusterHasAlert = false;
                                    markers.forEach(m => {
                                        if (m.options && m.options.maxFilling !== undefined && m.options.maxFilling > clusterMaxFilling) {
                                            clusterMaxFilling = m.options.maxFilling;
                                        }
                                        if (m.options && m.options.hasWarning) clusterHasWarning = true;
                                        if (m.options && m.options.hasAlert) clusterHasAlert = true;
                                    });
                                    const color = getHeatmapColor(clusterMaxFilling);
                                    const islandIcon = `${urlStatic}/images/icons/waypoints/1-island.svg`;
                                    const count = cluster.getChildCount();
                                    const textColor = (clusterMaxFilling > 50 && clusterMaxFilling <= 80) ? "#111" : "#fff";


                                    //Island Group Icon

                                    let clusterWarningHtml = "";
                                    if (clusterHasWarning) {
                                        clusterWarningHtml = renderToString(
                                            <div title="Aviso de desbordamiento" style={{ "position": "absolute", "bottom": "0px", "right": "0px", "width": "18px", "height": "18px", "display": "flex", "alignItems": "center", "justifyContent": "center", "zIndex": "10" }}>
                                                <FontAwesomeIcon icon={faCircleExclamation} style={{ "color": "#ffc107", "fontSize": "14px", "backgroundColor": "white", "border": "2px solid white", "borderRadius": "50%" }} />
                                            </div>);
                                    }

                                    let clusterAlertHtml = "";
                                    if (clusterHasAlert) {
                                        clusterAlertHtml = renderToString(
                                            <div title="Aviso de alerta generada" style={{ "position": "absolute", "bottom": "0px", "left": "0px", "width": "18px", "height": "18px", "display": "flex", "alignItems": "center", "justifyContent": "center", "zIndex": "10" }}>
                                                <FontAwesomeIcon icon={faCircleExclamation} style={{ "color": "#dc3545", "fontSize": "14px", "backgroundColor": "white", "border": "2px solid white", "borderRadius": "50%" }} />
                                            </div>);
                                    }

                                    return L.divIcon({
                                        html: `
                                        <div style="position: relative; width: 50px; height: 50px; background-image: url('${islandIcon}'); background-size: contain; background-repeat: no-repeat; background-position: bottom center;">
                                            <div style="position: absolute; background-color: ${color}; top: 0px; right: 0px; width: 24px; height: 24px; border-radius: 50%; display: flex; text-align: center; justify-content: center; align-items: center; color: ${textColor}; font-size: 12px; font-weight: bold; border: 2px solid white; box-shadow: 0 0 5px rgba(0,0,0,0.5);">
                                                ${count}
                                            </div>
                                            ${clusterWarningHtml}
                                            ${clusterAlertHtml}
                                        </div>`,
                                        className: 'custom-cluster-icon',
                                        iconSize: L.point(50, 50),
                                        iconAnchor: L.point(25, 50)
                                    });
                                }}
                            >
                                {Object.entries(islands).flatMap(([key]) => {
                                    const island = islands[key];
                                    const isHeatmapMode = currentZoom >= 18;
                                    const isMesoMode = currentZoom >= 14 && currentZoom < 18;
                                    const maxFilling = island.assets.length > 0 ? Math.max(...island.assets.map(a => a.filling || 0)) : 0;
                                    const islandColor = getHeatmapColor(maxFilling);

                                    const popupContent = (
                                        <Popup>
                                            <p>
                                                <Link to={"/data-view/island/" + key + "/"} className="slx-link">
                                                    {island.name}
                                                </Link>
                                            </p>
                                            {island.assets.map((as, ix) => (
                                                <div className="d-inline-block" key={as.id + "-" + ix}>
                                                    {as.device ? (
                                                        <Link to={"/data-view/data-container/" + as.uid + "/"}>
                                                            <ContainerMarkerSVG type={as.type} percent={as.filling} />
                                                        </Link>
                                                    ) : (
                                                        <ContainerMarkerSVG type={as.type} percent={as.filling} />
                                                    )}
                                                </div>
                                            ))}
                                        </Popup>
                                    );

                                    //ContainerIcon


                                    if (isHeatmapMode) {
                                        return island.assets.map((as, index) => {
                                            const offsetCoords = getOffsetCoords(island.coords, index, island.assets.length, 0.00015);
                                            const { url: iconUrl, fillColor: typeFill, borderColor: typeBorder, backgroundColor: typeBg } = getWaypointIconData(as.type);

                                            const filling = Math.round(as.filling || 0);

                                            let warningHtml = "";
                                            if (filling >= 80) {
                                                warningHtml = renderToString(
                                                    <div title="Aviso de desbordamiento" style={{ "position": "absolute", "bottom": "-4px", "right": "-4px", "width": "18px", "height": "18px", "display": "flex", "alignItems": "center", "justifyContent": "center", "zIndex": "4" }}>
                                                        <FontAwesomeIcon icon={faCircleExclamation} style={{ "color": "#ffc107", "fontSize": "14px", "backgroundColor": "white", "border": "2px solid white", "borderRadius": "50%" }} />
                                                    </div>);
                                            }

                                            let alertHtml = "";
                                            if (as.alert) {
                                                alertHtml = renderToString(
                                                    <div title="Aviso de alerta generada" style={{ "position": "absolute", "bottom": "-4px", "left": "-4px", "width": "18px", "height": "18px", "display": "flex", "alignItems": "center", "justifyContent": "center", "zIndex": "4" }}>
                                                        <FontAwesomeIcon icon={faCircleExclamation} style={{ "color": "#dc3545", "fontSize": "14px", "backgroundColor": "white", "border": "2px solid white", "borderRadius": "50%" }} />
                                                    </div>);
                                            }

                                            const containerIcon = new L.DivIcon({
                                                className: "custom-container-icon",
                                                html: `
                                                <div style="position: relative; width: 44px; height: 44px; display: flex; align-items: center; justify-content: center;">
                                                    <div style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; background: linear-gradient(to top, ${typeFill} ${filling}%, ${typeBg} ${filling}%); border: 1px solid ${typeBorder};"></div>
                                                    
                                                    <div style="position: relative; width: 24px; height: 24px; background-image: url('${iconUrl}'); background-size: contain; background-repeat: no-repeat; background-position: center; z-index: 2;"></div>
                                                    
                                                    <div style="position: absolute; top: -4px; right: -4px; width: 22px; height: 22px; background-color: #fff; border: 1px solid ${typeBorder}; color: black; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 10px; z-index: 3; box-shadow: 0 1px 3px rgba(0,0,0,0.3);">
                                                        ${filling}
                                                    </div>

                                                    ${warningHtml}
                                                    ${alertHtml}
                                                </div>
                                                `,
                                                iconSize: [44, 44],
                                                iconAnchor: [22, 22],
                                                popupAnchor: [0, -22]
                                            });

                                            return (
                                                <Marker
                                                    key={key + "-" + as.id}
                                                    position={offsetCoords}
                                                    icon={containerIcon}
                                                    hasWarning={filling >= 80}
                                                    hasAlert={!!as.alert}
                                                >
                                                    {popupContent}
                                                </Marker>
                                            );
                                        });
                                    }

                                    //islandIcon

                                    const islandHasWarning = island.assets.some(a => (a.filling || 0) >= 80);
                                    const islandHasAlert = island.assets.some(a => a.alert);

                                    let islandWarningHtml = "";
                                    if (islandHasWarning) {
                                        islandWarningHtml = renderToString(
                                            <div title="Aviso de desbordamiento" style={{ "position": "absolute", "bottom": "-4px", "right": "-4px", "width": "18px", "height": "18px", "display": "flex", "alignItems": "center", "justifyContent": "center", "zIndex": "10" }}>
                                                <FontAwesomeIcon icon={faCircleExclamation} style={{ "color": "#ffc107", "fontSize": "14px", "backgroundColor": "white", "border": "2px solid white", "borderRadius": "50%" }} />
                                            </div>);
                                    }

                                    let islandAlertHtml = "";
                                    if (islandHasAlert) {
                                        islandAlertHtml = renderToString(
                                            <div title="Aviso de alerta generada" style={{ "position": "absolute", "bottom": "-4px", "left": "-4px", "width": "20px", "height": "20px", "display": "flex", "align-items": "center", "justify-content": "center", "z-index": "10" }}>
                                                <FontAwesomeIcon icon={faCircleExclamation} style={{ "color": "#dc3545", "font-size": "14px", "backgroundColor": "white", "border": "2px solid white", "borderRadius": "50%" }} />
                                            </div>);
                                    }

                                    const badgeHtml = isMesoMode ? `<div class="bg-primary text-white rounded-circle shadow" style="width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; position: absolute; top: -10px; right: -10px; font-size: 12px; font-weight: bold; border: 2px solid white;">${island.assets.length}</div>` : "";

                                    const iconWithBadge = new L.DivIcon({
                                        className: "custom-island-icon",
                                        html: `<div style="position: relative; width: 32px; height: 32px;">
                                            <div style="width: 32px; height: 32px; border-radius: 50%; background-color: ${islandColor}; opacity: 0.9; border: 2px solid white; box-shadow: 0 0 10px ${islandColor};"></div>
                                            ${badgeHtml}
                                            ${islandWarningHtml}
                                            ${islandAlertHtml}
                                           </div>`,
                                        iconSize: [32, 32],
                                        iconAnchor: [16, 16],
                                        popupAnchor: [0, -16]
                                    });

                                    return [
                                        <Marker
                                            position={island.coords}
                                            key={key}
                                            icon={iconWithBadge}
                                            maxFilling={maxFilling}
                                            hasWarning={islandHasWarning}
                                            hasAlert={islandHasAlert}
                                        >
                                            {popupContent}
                                        </Marker>
                                    ];
                                })}
                            </MarkerClusterGroup>
                        )}
                    </MapContainer>
                </>
            );
        default:
            return <></>;
    }
};

export default MapHomeWaste;
